import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { alertService } from '@/services/alert.service'
import type { AlertCreateDto } from '@/types/alert.types'

export const useAlerts = () => {
  return useQuery({
    queryKey: ['alerts'],
    queryFn: () => alertService.getAlerts(),
    staleTime: 30000
  })
}

export const useAlert = (alertId: string) => {
  return useQuery({
    queryKey: ['alert', alertId],
    queryFn: () => alertService.getAlert(alertId),
    enabled: !!alertId
  })
}

export const useCreateAlert = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (alert: AlertCreateDto) => alertService.createAlert(alert),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    }
  })
}

export const useUpdateAlert = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ alertId, alert }: { alertId: string; alert: Partial<AlertCreateDto> }) =>
      alertService.updateAlert(alertId, alert),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['alert', variables.alertId] })
    }
  })
}

export const useDeleteAlert = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (alertId: string) => alertService.deleteAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    }
  })
}

export const useTriggeredAlerts = (dismissed?: boolean) => {
  return useQuery({
    queryKey: ['triggered-alerts', dismissed],
    queryFn: () => alertService.getTriggeredAlerts(dismissed),
    staleTime: 30000,
    refetchInterval: 60000
  })
}

export const useDismissAlert = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (triggeredAlertId: string) => alertService.dismissAlert(triggeredAlertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['triggered-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] })
    }
  })
}

export const useAlertDevices = (alertId: string) => {
  return useQuery({
    queryKey: ['alert-devices', alertId],
    queryFn: () => alertService.getAlertDevices(alertId),
    enabled: !!alertId
  })
}

export const useAlertRecipients = (alertId: string) => {
  return useQuery({
    queryKey: ['alert-recipients', alertId],
    queryFn: () => alertService.getAlertRecipients(alertId),
    enabled: !!alertId
  })
}

export const useAssignAlertDevice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ alertId, deviceId }: { alertId: string; deviceId: string }) =>
      alertService.assignDevice(alertId, deviceId),
    onSuccess: (_, { alertId }) => {
      queryClient.invalidateQueries({ queryKey: ['alert-devices', alertId] })
      queryClient.invalidateQueries({ queryKey: ['alert', alertId] })
    }
  })
}

export const useUnassignAlertDevice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ alertId, deviceId }: { alertId: string; deviceId: string }) =>
      alertService.unassignDevice(alertId, deviceId),
    onSuccess: (_, { alertId }) => {
      queryClient.invalidateQueries({ queryKey: ['alert-devices', alertId] })
      queryClient.invalidateQueries({ queryKey: ['alert', alertId] })
    }
  })
}

export const useAssignAlertRecipient = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ alertId, userId }: { alertId: string; userId: string }) =>
      alertService.assignRecipient(alertId, userId),
    onSuccess: (_, { alertId }) => {
      queryClient.invalidateQueries({ queryKey: ['alert-recipients', alertId] })
      queryClient.invalidateQueries({ queryKey: ['alert', alertId] })
    }
  })
}

export const useUnassignAlertRecipient = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ alertId, userId }: { alertId: string; userId: string }) =>
      alertService.unassignRecipient(alertId, userId),
    onSuccess: (_, { alertId }) => {
      queryClient.invalidateQueries({ queryKey: ['alert-recipients', alertId] })
      queryClient.invalidateQueries({ queryKey: ['alert', alertId] })
    }
  })
}
